/**
 * Workout Today Action - WHAT and WHEN Sections
 *
 * Edit mode UI for sending upcoming workout reminder.
 * Reminds clients about their scheduled workouts for the day.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-workout.hbs
 *
 * Fields:
 * - delayInterval: Hour of day to send (stored as hour value, e.g., 8 for 8 AM)
 * - media: Delivery methods (email, push, SMS)
 */

import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';

import { ActionWhatSectionProps } from '../automationActions';

const HOURS_OF_DAY = Array.from({ length: 24 }, (_, i) => {
  let name = `${i} AM`;
  if (i === 0) name = '12 AM';
  else if (i === 12) name = '12 PM';
  else if (i > 12) name = `${i - 12} PM`;
  return { value: String(i), label: name };
});

const formatHour = (hour: number): string => {
  const entry = HOURS_OF_DAY.find((h) => h.value === String(hour));
  return entry?.label ?? `${hour}`;
};

/**
 * WorkoutToday WHAT Section - Edit mode
 *
 * This action's main config is the time of day to send (in When section)
 * and the delivery method (in How section). No specific "what" config needed.
 */
export const WorkoutTodayWhatSection: FC<ActionWhatSectionProps> = () => {
  const { t } = useTranslation();

  return (
    <Typography color="text.secondary" fontStyle="italic">
      {t('automations.workoutToday.description')}
    </Typography>
  );
};

/**
 * WorkoutToday What Summary Component
 */
export const WorkoutTodayWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = () => {
  const { t } = useTranslation();

  return <>{t('automations.workoutToday.summary')}</>;
};

/**
 * WorkoutToday WHEN Section - Edit mode
 * "at [hour of day]" selector matching v2 behavior
 */
export const WorkoutTodayWhenSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Typography variant="body2">
        {t('automations.workoutToday.at')}
      </Typography>
      <TextField
        value={String(automation.delayInterval ?? 8)}
        onChange={(e) =>
          setFieldValue(
            `automations.${index}.delayInterval`,
            parseInt(e.target.value, 10) || 0
          )
        }
        options={HOURS_OF_DAY}
        size="small"
        sx={{ minWidth: 120 }}
      />
    </Box>
  );
};

/**
 * WorkoutToday WHEN Summary
 */
export const WorkoutTodayWhenSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const hour = automation.delayInterval ?? 0;

  return (
    <>
      {t('automations.workoutToday.whenSummary', { time: formatHour(hour) })}
    </>
  );
};
